import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../useApi';
import { ErrorNotice } from '../components/ErrorNotice';

// Every support action, with its reason.
//
// Failures are listed alongside successes deliberately. An action that was
// attempted and refused is usually the more interesting entry — it is the one
// that explains why something is still in the state it is in.

interface Entry {
  supportActionId: string;
  action: string;
  accountId: string | null;
  tenantId: string | null;
  stripeObjectId: string | null;
  reason: string;
  actorName: string | null;
  actorObjectId: string;
  succeeded: boolean;
  detail: string | null;
  createdAt: string;
}

export function AuditPage() {
  const api = useApi();
  const [actions, setActions] = useState<Entry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!api) return;
    try {
      const result = await api.get<{ actions: Entry[] }>('/support/audit');
      setActions(result.actions);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the audit log.');
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!actions) {
    return (
      <>
        <ErrorNotice message={error} />
        {!error && <p className="muted">Loading…</p>}
      </>
    );
  }

  return (
    <>
      <ErrorNotice message={error} />
      <div className="card">
        {actions.length === 0 ? (
          <p className="muted">No support actions have been taken yet.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Action</th>
                <th>Reason</th>
                <th>Who</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {actions.map((entry) => (
                <tr key={entry.supportActionId}>
                  <td className="small">{new Date(entry.createdAt).toLocaleString()}</td>
                  <td className="mono small">
                    {entry.action}
                    {entry.stripeObjectId && (
                      <>
                        <br />
                        <span className="muted">{entry.stripeObjectId}</span>
                      </>
                    )}
                  </td>
                  <td className="small">
                    {entry.reason}
                    {entry.detail && (
                      <>
                        <br />
                        <span className="muted">{entry.detail}</span>
                      </>
                    )}
                  </td>
                  <td className="small">{entry.actorName ?? entry.actorObjectId}</td>
                  <td>{entry.succeeded ? 'ok' : <strong>failed</strong>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
